import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
  Users,
  Plus,
  Loader2,
  Mail,
  Phone,
  Building,
  Edit,
  Trash2,
  Search,
  UserCheck,
  UserX,
  Briefcase,
  Calendar
} from "lucide-react";

export default function GestionHumanaPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    department: "",
    position: "",
    role: "analyst",
    is_active: true
  });
  const { getAuthHeaders, user: currentUser } = useAuth();

  const departments = [
    "Ventas",
    "Operaciones", 
    "Administración",
    "Tecnología",
    "Recursos Humanos",
    "Finanzas",
    "Marketing"
  ];

  const roles = [
    { value: "admin", label: "Administrador" },
    { value: "analyst", label: "Analista" },
    { value: "viewer", label: "Visualizador" }
  ];

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      // Use users endpoint - employees are users
      const res = await axios.get(`${API}/users`, getAuthHeaders());
      setEmployees(res.data);
    } catch (err) {
      toast.error("Error al cargar empleados");
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      phone: "",
      department: "",
      position: "",
      role: "analyst",
      is_active: true
    });
    setEditingEmployee(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      email: employee.email,
      password: "",
      phone: employee.phone || "",
      department: employee.department || "",
      position: employee.position || "",
      role: employee.role || "analyst",
      is_active: employee.is_active !== false
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error("Nombre y email son requeridos");
      return;
    }

    if (!editingEmployee && !formData.password) {
      toast.error("La contraseña es requerida para nuevos empleados");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        is_active: formData.is_active,
        phone: formData.phone,
        department: formData.department,
        position: formData.position
      };

      if (formData.password) {
        payload.password = formData.password;
      }

      if (editingEmployee) {
        await axios.put(`${API}/users/${editingEmployee.id}`, payload, getAuthHeaders());
        toast.success("Empleado actualizado");
      } else {
        payload.password = formData.password;
        await axios.post(`${API}/users`, payload, getAuthHeaders());
        toast.success("Empleado agregado");
      }
      setShowDialog(false);
      resetForm();
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar el empleado");
    } finally {
      setSaving(false);
    }
  };

  const toggleEmployeeStatus = async (employee) => {
    try {
      await axios.put(`${API}/users/${employee.id}`, {
        ...employee,
        is_active: !employee.is_active
      }, getAuthHeaders());
      toast.success(employee.is_active ? "Empleado desactivado" : "Empleado activado");
      fetchEmployees();
    } catch (err) {
      toast.error("Error al cambiar estado");
    }
  };

  const deleteEmployee = async (id) => {
    if (id === currentUser?.id) {
      toast.error("No puedes eliminar tu propia cuenta");
      return;
    }
    if (!window.confirm("¿Estás seguro de eliminar este empleado?")) return;
    try {
      await axios.delete(`${API}/users/${id}`, getAuthHeaders());
      toast.success("Empleado eliminado");
      setEmployees(employees.filter(e => e.id !== id));
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getRoleBadge = (role) => {
    const roleInfo = roles.find(r => r.value === role);
    const colors = {
      admin: "bg-red-100 text-red-700",
      analyst: "bg-blue-100 text-blue-700",
      viewer: "bg-gray-100 text-gray-700"
    };
    return (
      <Badge className={`${colors[role] || colors.analyst} border-0`}>
        {roleInfo?.label || role}
      </Badge>
    );
  };

  const uniqueDepartments = [...new Set(employees.map(e => e.department).filter(Boolean))];

  const filteredEmployees = employees.filter(e => {
    const matchesSearch = e.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         e.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         e.position?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDept = departmentFilter === "all" || e.department === departmentFilter;
    const matchesStatus = statusFilter === "all" || 
                         (statusFilter === "active" && e.is_active !== false) ||
                         (statusFilter === "inactive" && e.is_active === false);
    return matchesSearch && matchesDept && matchesStatus;
  });

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="gestion-humana-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Gestión Humana
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Administra el personal de tu organización
            </p>
          </div>
          <Button 
            onClick={openNewDialog}
            className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
            data-testid="new-employee-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Empleado
          </Button>
        </div>

        {/* Stats - Clickable filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card 
            className={`border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === "all" && !searchQuery ? "ring-2 ring-blue-500" : ""}`}
            onClick={() => { setStatusFilter("all"); setSearchQuery(""); setDepartmentFilter("all"); }}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{employees.length}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Total Empleados</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === "active" ? "ring-2 ring-green-500" : ""}`}
            onClick={() => { setStatusFilter("active"); setSearchQuery(""); }}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {employees.filter(e => e.is_active !== false).length}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Activos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === "inactive" ? "ring-2 ring-orange-500" : ""}`}
            onClick={() => { setStatusFilter("inactive"); setSearchQuery(""); }}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                  <UserX className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {employees.filter(e => e.is_active === false).length}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Inactivos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                  <Building className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{uniqueDepartments.length}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Departamentos</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <Card className="border-slate-200 dark:border-slate-700 shadow-sm mb-6 dark:bg-slate-800">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Buscar por nombre, email o cargo..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                  data-testid="search-employees"
                />
              </div>
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger className="w-full md:w-[200px] border-slate-200 dark:border-slate-600">
                  <Building className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Departamento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los departamentos</SelectItem>
                  {uniqueDepartments.map(dept => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Employees List */}
        <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
          <CardHeader className="border-b border-slate-100 dark:border-slate-700">
            <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white font-['Manrope']">
              Directorio de Empleados
            </CardTitle>
            <CardDescription className="dark:text-slate-400">
              {filteredEmployees.length} empleado(s) encontrado(s)
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 dark:text-slate-400 mb-4">
                  {searchQuery || departmentFilter !== "all" 
                    ? "No se encontraron empleados con esos criterios" 
                    : "No hay empleados registrados"}
                </p>
                {!searchQuery && departmentFilter === "all" && (
                  <Button onClick={openNewDialog} variant="outline" className="border-slate-200">
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar primer empleado
                  </Button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {filteredEmployees.map((employee) => (
                  <div
                    key={employee.id}
                    className="flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                    data-testid={`employee-row-${employee.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        employee.is_active !== false ? 'bg-[#0C314C]' : 'bg-slate-400'
                      }`}>
                        <span className="text-white font-semibold text-lg">
                          {employee.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900 dark:text-white">{employee.name}</p>
                          {getRoleBadge(employee.role)}
                          {employee.is_active === false && (
                            <Badge className="bg-slate-200 text-slate-600 border-0">Inactivo</Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500 dark:text-slate-400 mt-1">
                          <span className="flex items-center gap-1 min-w-0 break-all">
                            <Mail className="w-3 h-3 shrink-0" />
                            {employee.email}
                          </span>
                          {employee.department && (
                            <span className="flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              {employee.department}
                            </span>
                          )}
                          {employee.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {employee.phone}
                            </span>
                          )}
                        </div>
                        {employee.position && (
                          <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                            <Briefcase className="w-3 h-3" />
                            {employee.position}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400 mr-2 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(employee.created_at)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleEmployeeStatus(employee)}
                        className={employee.is_active !== false 
                          ? "text-green-600 hover:text-green-700 hover:bg-green-50" 
                          : "text-orange-600 hover:text-orange-700 hover:bg-orange-50"}
                        title={employee.is_active !== false ? "Desactivar" : "Activar"}
                      >
                        {employee.is_active !== false ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(employee)}
                        className="text-slate-500 hover:text-slate-900 dark:hover:text-white"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {employee.id !== currentUser?.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteEmployee(employee.id)}
                          className="text-slate-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Manrope']">
                {editingEmployee ? "Editar Empleado" : "Nuevo Empleado"}
              </DialogTitle>
              <DialogDescription>
                {editingEmployee ? "Actualiza los datos del empleado" : "Completa los datos del nuevo empleado"}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre completo *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Juan Pérez"
                  data-testid="employee-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="juan@qasesores.com"
                  data-testid="employee-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">
                  Contraseña {editingEmployee ? "(dejar vacío para no cambiar)" : "*"}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="••••••••"
                  data-testid="employee-password-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="department">Departamento</Label>
                  <Select value={formData.department} onValueChange={(value) => setFormData({...formData, department: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map(dept => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="role">Rol del Sistema</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                    <SelectTrigger data-testid="employee-role-select">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map(role => (
                        <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="position">Cargo</Label>
                <Input
                  id="position"
                  value={formData.position}
                  onChange={(e) => setFormData({...formData, position: e.target.value})}
                  placeholder="Ejecutivo de Cuentas"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+1 234 567 8900"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                data-testid="save-employee-button"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
